import { Box, Typography } from '@mui/material'
import BigNumber from 'bignumber.js'
import { TaskNameId, _1e6 } from 'constants/constants'
import { PERCENT, ZERO } from 'constants/number'
import { useOraclePrice } from 'hooks/useOracle'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import LiquidFillGauge from 'react-liquid-gauge'
import { gatewayService } from 'services/gatewayService'
import { poolService } from 'services/poolService'
import { betaLaunchStore, oracleStore, taskStore } from 'stores'
import { theme } from 'theme'
import HorizontalItem from './container/HorizontalItem'

export const CardTVL = observer(() => {
  const { maxTVL } = betaLaunchStore

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
      }}
    >
      <HorizontalItem>
        <Typography variant="h6"> Capacity </Typography>
        <Typography variant="body2">
          Max{' '}
          <strong
            style={{ color: theme.palette.text.primary, fontWeight: 700 }}
          >
            ${maxTVL.toNumber().toLocaleString('en-US')}
          </strong>
        </Typography>
      </HorizontalItem>
      <TVLgauge width={190} fontScale="small" />
    </Box>
  )
})

const fillColor = 'rgba(255,255,255,0.32)'
const gradientStops = [
  {
    key: '0%',
    stopColor: '#E36139',
    stopOpacity: 1,
    offset: '.117',
  },
  {
    key: '100%',
    stopColor: '#F4771D',
    stopOpacity: 1,
    offset: '0.908',
  },
]

const TVLgauge = observer(
  ({ width = 420, fontScale = 'big', ...props }: any) => {
    const { currentTVL, maxTVL } = betaLaunchStore
    const poolAddr = poolService.poolAddress
    const prices = oracleStore.getPrices(poolAddr)
    const value = useMemo(() => {
      if (!currentTVL || !maxTVL || maxTVL.eq(ZERO)) return 0
      return currentTVL.div(maxTVL).times(PERCENT).toNumber() || 0
    }, [currentTVL, maxTVL])
    useOraclePrice({ poolAddr })

    useEffect(() => {
      const fetchData = async () => {
        const [supplyMarketList, gatewayConfig] = await Promise.all([
          poolService.getMarketList(),
          gatewayService.getConfig(),
        ])

        betaLaunchStore.setMaxTVL(
          new BigNumber(gatewayConfig.cap_tvl).div('1e6'),
        )
        betaLaunchStore.setCurrentTVL(
          BigNumber.sum(
            ...supplyMarketList.map((market) =>
              new BigNumber(market.total_credit)
                .plus(market.total_loan)
                .div(_1e6)
                .times(prices ? prices[market.underlying] ?? 1 : 1),
            ),
            0,
          ),
        )
      }
      taskStore.updateTask(fetchData, TaskNameId.tvl)
      fetchData()
    }, [prices])

    const size = useMemo(() => {
      const a = window.innerWidth - 100
      const b = width
      if (a > b) return b
      else return a
    }, [width])

    const [tvl, setTvl] = useState(0)
    useEffect(() => {
      setTvl(parseFloat(BigNumber.min(currentTVL, maxTVL).toFixed(2)))
    }, [currentTVL, maxTVL])
    useEffect(() => {
      let timeId = setInterval(() => setTvl(tvl + 0.01), 150)
      return () => {
        clearInterval(timeId)
      }
    }, [tvl])

    return (
      <Box
        sx={{
          width: 'min-content',
          margin: 'auto',
          background: 'white',
          borderRadius: '100%',
          backgroundOrigin: 'border-box',
        }}
      >
        <Box
          sx={{
            transform: 'scale(1.1)',
          }}
        >
          <LiquidFillGauge
            {...props}
            width={size}
            height={size}
            value={value}
            percent="%"
            textSize={1}
            textOffsetX={0}
            textOffsetY={-5}
            textRenderer={({
              value,
              width,
              height,
              textSize,
              percent,
            }: any) => {
              value = Math.round(value)

              const fontSize = {
                small: {
                  caption: 12,
                  body: 20,
                },
                big: {
                  caption: 16,
                  body: 24,
                },
              }[fontScale as string]

              return (
                <tspan x="0" y="0">
                  <tspan x="0" style={{ fontSize: fontSize?.caption }}>
                    Current value supplied
                  </tspan>
                  <tspan
                    x="0"
                    dy="1.2em"
                    style={{ fontSize: fontSize?.body, fontWeight: 600 }}
                  >
                    ${tvl.toLocaleString('en-US')}
                  </tspan>
                </tspan>
              )
            }}
            riseAnimation
            waveAnimation
            waveFrequency={2}
            waveAmplitude={3}
            gradient
            gradientStops={gradientStops}
            circleStyle={{
              fill: fillColor,
            }}
            waveStyle={{
              fill: fillColor,
            }}
            textStyle={{
              fill: theme.palette.text.primary,
            }}
            waveTextStyle={{
              fill: 'white',
            }}
          />
        </Box>
      </Box>
    )
  },
)

export default TVLgauge
